import html2canvas from 'html2canvas'
import React from 'react'

import { apiGeneratePdfFromHtml } from 'api/adcritterApi'
import { AIPlan, AIPlanStub } from 'api/interfaces/AIPlan'
import { AIPlanExecutiveSummarySection } from 'api/interfaces/AIPlanExecutiveSummarySection'
import { AIPlanPersonasSection } from 'api/interfaces/AIPlanPersonasSection'
import { AcDomain } from 'api/interfaces/AcDomain'
import { extractHtmlWithStyles } from 'utils/htmlExtractor'
import { generatePdfBlob } from 'utils/pdfDownloader'
import { processSvgImagesInElement } from 'utils/svgConversionUtils'

import AIPlanExecutiveSummaryPdf from '../features/planner/pages/AIPlanPage/components/AIPlanExecutiveSummaryPdf'
import AIPlanMediaPlanPdf from '../features/planner/pages/AIPlanPage/components/AIPlanMediaPlanPdf'
import AIPlanPersonasPdf from '../features/planner/pages/AIPlanPage/components/AIPlanPersonasPdf'
import AIPlanProjectionsPdf from '../features/planner/pages/AIPlanPage/components/Projections/AIPlanProjectionsPdf'

export interface LogoInput {
  dataUrl: string
  width: number
  height: number
}

export interface AIPlanPdfGenerationContext {
  aiPlan: AIPlan
  aiPlanStub: AIPlanStub
  organizationName: string
  accountName: string
  executiveSummarySection?: AIPlanExecutiveSummarySection
  personasSection?: AIPlanPersonasSection
  domains?: AcDomain[]
  agencyLogo?: LogoInput
  clientLogo?: LogoInput
  isDirect?: boolean
}

export interface PdfGenerationResult {
  blob?: Blob
  success: boolean
  error?: string
}

export const generateAIPlanExecutiveSummaryPdf = async (
  isStaffPdf: boolean,
  context: AIPlanPdfGenerationContext,
  ganttChartRef?: React.RefObject<HTMLDivElement>
): Promise<PdfGenerationResult> => {
  try {
    let ganttChartImage: string | undefined

    if (!isStaffPdf && ganttChartRef?.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        ganttChartImage = canvas.toDataURL('image/png')
      } catch {
      }
    }

    const campaigns = context.aiPlanStub.campaignBuilders || []

    const blob = await generatePdfBlob(
      React.createElement(AIPlanExecutiveSummaryPdf, {
        aiPlan: context.aiPlan,
        campaigns,
        ganttChartImage,
        executiveSummarySection: context.executiveSummarySection
      })
    )

    return { blob, success: true }
  } catch (error) {
    return { success: false, error: `Executive Summary PDF failed: ${error instanceof Error ? error.message : String(error)}` }
  }
}

export const generateAIPlanMarketAnalysisPdf = async (
  _isStaffPdf: boolean,
  context: AIPlanPdfGenerationContext,
  printRef?: React.RefObject<HTMLDivElement>
): Promise<PdfGenerationResult> => {
  try {
    if (!printRef?.current) {
      throw new Error('Missing market analysis print reference')
    }

    let retryCount = 0
    const maxRetryCount = 50
    while (retryCount < maxRetryCount && !printRef?.current.querySelector('h2')) {
      retryCount++
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    const htmlContent = await extractHtmlWithStyles(printRef.current, {
      pageTitle: `${context.aiPlan.name} - Market Analysis`,
      includeStyles: true,
      sanitize: true,
      prependCustomStyles: `
          @page {
              size: letter;
              margin-top: 1.25in;
              margin-left: 1.75in;
              margin-bottom: 1.25in;
              margin-right: 1.75in;
          }
          * {
              box-sizing: border-box;
          }
          body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
          }
          body, body *:not(blockquote) {
              color: #333 !important;
          }
          html,body :not(thead):not(hr):not(code):not(pre) {
              background: white !important;
              background-color: white !important;
          }
          img {
              max-width: 100%;
              height: auto;
          }
          table {
              border-collapse: collapse;
              width: 100%;
          }
          th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
          }
          .market-analysis-pdf-content {
              position: static !important;
              left: auto !important;
          }
          .euiAccordion__arrow {
              display: none !important;
          }
          .euiButtonIcon {
              display: none !important;
          }
          [class*="addCustomSectionButton"] {
              display: none !important;
          }
          .euiAccordion__padding--l {
              padding: 0 !important;
          }
          .euiSpacer {
              display: none !important;
          }
          ul {
              list-style-type: disc !important;
              padding-left: 1.5rem !important;
              margin: 0 0 4pt 0 !important;
          }
          ol {
              list-style-type: decimal !important;
              padding-left: 1.5rem !important;
              margin: 0 0 4pt 0 !important;
          }
          li {
              margin: 0 0 2pt 0 !important;
          }
          blockquote {
              font-size: 12pt !important;
              line-height: 1.6 !important;
              margin: 8pt 0 !important;
              padding: 4pt 0 4pt 16pt !important;
              border-left: 4px solid #d3dae6 !important;
              color: #69707d !important;
          }
          hr {
              border: none !important;
              border-top: 1px solid #d3dae6 !important;
              margin: 12pt 0 !important;
          }
          code {
              font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important;
              font-size: 0.9em !important;
              padding: 2pt 4pt !important;
              background-color: #f5f7fa !important;
              border-radius: 3px !important;
          }
          pre {
              font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important;
              font-size: 11pt !important;
              line-height: 1.5 !important;
              margin: 8pt 0 !important;
              padding: 12pt !important;
              background-color: #f5f7fa !important;
              border-radius: 4px !important;
              overflow-x: auto !important;
              white-space: pre-wrap !important;
              word-wrap: break-word !important;
          }
          @media print {
              body {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
              }
          }`
    })

    const fileName = `${context.aiPlan.name}_Market_Analysis.pdf`
    const blob = await apiGeneratePdfFromHtml(fileName, htmlContent, 0, 0, 0, 0)

    return { blob, success: true }
  } catch (error) {
    return { success: false, error: `Market Analysis PDF failed: ${error instanceof Error ? error.message : String(error)}` }
  }
}

export const generateAIPlanPersonasPdf = async (
  _isStaffPdf: boolean,
  context: AIPlanPdfGenerationContext
): Promise<PdfGenerationResult> => {
  try {
    const blob = await generatePdfBlob(
      React.createElement(AIPlanPersonasPdf, {
        aiPlan: context.aiPlan,
        personaSection: context.personasSection
      })
    )

    return { blob, success: true }
  } catch (error) {
    return { success: false, error: `Personas PDF failed: ${error instanceof Error ? error.message : String(error)}` }
  }
}

export const generateAIPlanMediaPlanPdf = async (
  _isStaffPdf: boolean,
  context: AIPlanPdfGenerationContext,
  ganttChartRef?: React.RefObject<HTMLDivElement>,
  mediaplanRef?: React.RefObject<any>
): Promise<PdfGenerationResult> => {
  try {
    let ganttChartImage: string | undefined
    let campaignMapImages: Record<string, string | null> = {}

    if (ganttChartRef?.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        ganttChartImage = canvas.toDataURL('image/png')
      } catch {
        // Gantt chart capture is non-fatal
      }
    }

    if (mediaplanRef?.current?.getMapImages) {
      try {
        campaignMapImages = await mediaplanRef.current.getMapImages()
      } catch {
        // Map capture is non-fatal
      }
    }

    const campaigns = context.aiPlanStub.campaignBuilders || []

    const blob = await generatePdfBlob(
      React.createElement(AIPlanMediaPlanPdf, {
        campaigns,
        ganttChartImage,
        campaignMapImages
      })
    )

    return { blob, success: true }
  } catch (error) {
    return { success: false, error: `Media Plan PDF failed: ${error instanceof Error ? error.message : String(error)}` }
  }
}

const captureChartWithTimeout = async (
  ref: React.RefObject<HTMLDivElement> | undefined,
  options: { backgroundColor?: string; preDelay?: number; timeout?: number; processSvgs?: boolean; domains?: any[] } = {}
): Promise<string | undefined> => {
  if (!ref?.current) return undefined

  const { backgroundColor = '#ffffff', preDelay = 300, timeout = 30000, processSvgs = false, domains = [] } = options

  return Promise.race([
    (async () => {
      if (preDelay > 0) await new Promise(resolve => setTimeout(resolve, preDelay))

      if (processSvgs) {
        await processSvgImagesInElement(ref.current!, domains)
      }

      const canvas = await html2canvas(ref.current!, {
        backgroundColor,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      })
      return canvas.toDataURL('image/png')
    })(),
    new Promise<undefined>((_, reject) => setTimeout(() => reject(new Error('Chart capture timeout')), timeout))
  ])
}

export const generateAIPlanProjectionsPdf = async (
  isStaffPdf: boolean,
  context: AIPlanPdfGenerationContext,
  projectionRefs?: {
    deliveryMetricsRef?: React.RefObject<HTMLDivElement>
    connectedTvRef?: React.RefObject<HTMLDivElement>
    internetDisplayRef?: React.RefObject<HTMLDivElement>
    targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
    billboardLocationsRef?: React.RefObject<HTMLDivElement>
  },
  projectionMapRefs?: {
    targetedHouseholdMapRef?: React.RefObject<any>
    billboardMapRef?: React.RefObject<any>
  }
): Promise<PdfGenerationResult> => {
  try {
    const chartImages: Record<string, string> = {}
    const captureErrors: string[] = []

    try {
      const deliveryMetrics = await captureChartWithTimeout(projectionRefs?.deliveryMetricsRef, { preDelay: 300, timeout: 30000 })
      if (deliveryMetrics) chartImages.deliveryMetrics = deliveryMetrics
    } catch {
      captureErrors.push('Delivery metrics chart capture failed')
    }

    try {
      const connectedTv = await captureChartWithTimeout(projectionRefs?.connectedTvRef, { backgroundColor: '#E6EDF2', preDelay: 500, timeout: 30000 })
      if (connectedTv) chartImages.connectedTv = connectedTv
    } catch {
      captureErrors.push('CTV chart capture failed')
    }

    try {
      const internetDisplay = await captureChartWithTimeout(projectionRefs?.internetDisplayRef, { backgroundColor: '#E6EDF2', preDelay: 800, timeout: 45000, processSvgs: true })
      if (internetDisplay) chartImages.internetDisplay = internetDisplay
    } catch {
      captureErrors.push('Internet display chart capture failed')
    }

    try {
      const targetedHouseholds = await captureChartWithTimeout(projectionRefs?.targetedHouseholdsRef, { preDelay: 600, timeout: 40000 })
      if (targetedHouseholds) chartImages.targetedHouseholds = targetedHouseholds

      if (projectionMapRefs?.targetedHouseholdMapRef?.current?.getMapCanvas) {
        try {
          const mapCanvas = await projectionMapRefs.targetedHouseholdMapRef.current.getMapCanvas()
          if (mapCanvas) chartImages.targetedHouseholds = mapCanvas.toDataURL('image/png')
        } catch {
          // Map fallback is non-fatal
        }
      }
    } catch {
      captureErrors.push('Targeted households chart capture failed')
    }

    try {
      const billboardLocations = await captureChartWithTimeout(projectionRefs?.billboardLocationsRef, { preDelay: 600, timeout: 40000 })
      if (billboardLocations) chartImages.billboardLocations = billboardLocations

      if (projectionMapRefs?.billboardMapRef?.current?.getMapCanvas) {
        try {
          const mapCanvas = await projectionMapRefs.billboardMapRef.current.getMapCanvas()
          if (mapCanvas) chartImages.billboardLocations = mapCanvas.toDataURL('image/png')
        } catch {
          // Map fallback is non-fatal
        }
      }
    } catch {
      captureErrors.push('Billboard locations chart capture failed')
    }

    const blob = await generatePdfBlob(
      React.createElement(AIPlanProjectionsPdf, {
        stub: context.aiPlanStub,
        chartImages,
        isStaffPdf
      })
    )

    const errorSummary = captureErrors.length > 0 ? captureErrors.join('; ') : undefined

    return { blob, success: true, error: errorSummary }
  } catch (error) {
    return { success: false, error: `Projections PDF failed: ${error instanceof Error ? error.message : String(error)}` }
  }
}
